import { useEffect, useState } from 'react';
import { Button } from '../../../../ui/components/ui/button';
import LessonContent from '../lesson-content/lesson-content';
import VideoEmbed from '../video-embed/video-embed';

export interface LessonViewData {
  heading: string;
  videoUrl: string | null;
  textHtml: string | null;
}

export interface LessonViewProps {
  /** Parent module, shown in the breadcrumb. */
  moduleTitle: string;
  /** Lesson name, shown in the breadcrumb — the heading below can differ. */
  lessonTitle: string;
  lesson: LessonViewData;
  /** `preview` keeps navigation inert: in the editor there is no next lesson to go to. */
  variant?: 'live' | 'preview';
  /**
   * Fills the height its container gives it and scrolls only the body, keeping breadcrumb, heading
   * and navigation in place. For a bounded panel like the editor preview and the learner player.
   */
  fillHeight?: boolean;
  /** Label of the advance button — "Kurs abschließen" on the last lesson, otherwise the default. */
  nextLabel?: string;
  /** Disables the button and shows a busy label while the completion request is in flight. */
  isCompleting?: boolean;
  onNext?: () => void;
}

type LessonTab = 'video' | 'text';

/**
 * One lesson as learners see it. Shared on purpose: the admin preview and the learner page render
 * this exact component and only differ in where the data comes from.
 */
const LessonView = ({
  moduleTitle,
  lessonTitle,
  lesson,
  variant = 'live',
  fillHeight = false,
  nextLabel = 'nächste Lektion',
  isCompleting = false,
  onNext,
}: LessonViewProps) => {
  const hasVideo = !!lesson.videoUrl;
  const hasText = !!lesson.textHtml;
  const hasBoth = hasVideo && hasText;

  const [tab, setTab] = useState<LessonTab>(hasVideo ? 'video' : 'text');

  // Content can change under the tab — dropping a video while "video" is selected must not blank the view.
  useEffect(() => {
    setTab((current) => {
      if (current === 'video' && !hasVideo) return 'text';
      if (current === 'text' && !hasText) return 'video';
      return current;
    });
  }, [hasVideo, hasText]);

  const isEmpty = !hasVideo && !hasText;

  return (
    <div
      className={`bg-white rounded-2xl border border-[#E4E7EC] p-5 flex flex-col gap-4 ${fillHeight ? 'h-full min-h-0' : ''}`}
      style={{ boxShadow: '0px 4px 12.7px 0px rgba(0, 0, 0, 0.04)' }}>
      <div className="flex flex-col gap-2 shrink-0">
        <span className="text-[12px] text-[#888]">
          {moduleTitle} <span aria-hidden="true">›</span> {lessonTitle}
        </span>
        <h2 className="text-[20px] leading-[26px] font-bold text-[#12282A] text-balance">{lesson.heading}</h2>
      </div>

      {hasBoth && (
        <div className="inline-flex bg-[#F0F4FC] rounded-full p-1 gap-1 self-start shrink-0">
          {(['video', 'text'] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setTab(value)}
              aria-pressed={tab === value}
              className={`text-[12px] font-semibold px-3.5 py-1.5 rounded-full transition-colors ${
                tab === value ? 'bg-white text-[#12282A] shadow-sm' : 'text-[#888] hover:text-[#12282A]'
              }`}>
              {value === 'video' ? 'Videoansicht' : 'Textansicht'}
            </button>
          ))}
        </div>
      )}

      {/* The only scrolling region when bounded. Unbounded there is no overflow, so it never kicks in. */}
      <div className={fillHeight ? 'flex-1 min-h-0 overflow-y-auto' : ''}>
        {isEmpty ? (
          <div className="rounded-xl border border-dashed border-[#E4E7EC] py-10 text-center text-[13px] text-[#888]">
            Noch kein Inhalt — füge ein Video oder einen Text hinzu.
          </div>
        ) : tab === 'video' ? (
          <VideoEmbed url={lesson.videoUrl} />
        ) : (
          <LessonContent html={lesson.textHtml} />
        )}
      </div>

      <Button onClick={onNext} disabled={variant === 'preview' || isCompleting} className="self-center shrink-0">
        {isCompleting ? 'Speichert…' : nextLabel}
      </Button>
    </div>
  );
};

export default LessonView;
